import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Box,
  ChevronDownIcon,
  ChevronUpIcon,
  Option,
  RawButton,
  Text,
  useClickOutside,
} from '@common-fe/common-fe';

import { IEmployerCalendarEventDtoCategoryEnum } from '@/dtos';

interface Props {
  value: IEmployerCalendarEventDtoCategoryEnum | null;
  disabled?: boolean;
  onChangeValue: (value: IEmployerCalendarEventDtoCategoryEnum) => void;
}

const OPTIONS: Option[] = [
  {
    key: '',
    value: 'All Events',
  },
  {
    key: IEmployerCalendarEventDtoCategoryEnum.PLAN_DATES,
    value: 'Plan Dates',
  },
  {
    key: IEmployerCalendarEventDtoCategoryEnum.FUNDING,
    value: 'Funding',
  },
];
const EventTypeDropdown: React.FC<Props> = ({ value = '', onChangeValue, disabled }) => {
  const [expanded, setExpanded] = useState(false);
  const handleToggle = () => {
    setExpanded(!expanded);
  };
  const ref = useRef(null);
  useClickOutside(ref, () => {
    setExpanded(false);
  });

  const title = useMemo(() => {
    if (value) {
      const found = OPTIONS.find((item) => item.key === value);
      if (found) {
        return `${found.value}`;
      }
    }
    return 'All Events';
  }, [value]);
  const handlePickItem = useCallback(
    (key: IEmployerCalendarEventDtoCategoryEnum) => () => {
      if (value === key) {
        onChangeValue('' as IEmployerCalendarEventDtoCategoryEnum);
      } else {
        onChangeValue(key as IEmployerCalendarEventDtoCategoryEnum);
      }

      setExpanded(false);
    },
    [onChangeValue, value]
  );
  return (
    <Box data-testid="event-type-dropdown" style={{ position: 'relative' }}>
      <RawButton
        data-testid="event-type-dropdown_toggle"
        onClick={disabled && !value ? () => {} : handleToggle}
      >
        <Box direction="row" align="center" style={{ position: 'relative' }}>
          <Text
            data-testid="event-type-dropdown_title"
            color={disabled && !value ? 'textDisabled' : 'textAccent'}
            weight={400}
          >
            {title}
          </Text>
          <Box align="center" justify="center" margin={{ left: 'spacing4' }}>
            {expanded ? (
              <ChevronUpIcon color={disabled && !value ? 'iconDisabled' : 'iconPrimary'} size="24px" />
            ) : (
              <ChevronDownIcon size="24px" color={disabled && !value ? 'iconDisabled' : 'iconPrimary'} />
            )}
          </Box>
        </Box>
      </RawButton>
      {expanded && (
        <Box
          ref={ref}
          data-testid="event-type-dropdown_menu"
          direction="column"
          background="canvas"
          elevation="active"
          round="container2Round"
          pad="spacing12"
          width={{ min: '120px' }}
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            zIndex: 101,
          }}
        >
          {OPTIONS.map((item) => {
            const isActive = value === item.key || (!value && item.key === '');
            return (
              <RawButton
                key={item.key}
                data-testid={`event-type-dropdown_option-${item.key}`}
                onClick={handlePickItem(item.key as IEmployerCalendarEventDtoCategoryEnum)}
              >
                <Box pad={{ vertical: 'spacing4', horizontal: 'spacing8' }}>
                  <Text color={isActive ? 'accentActive' : 'textBody'} weight={isActive ? 700 : 400}>
                    {`${item.value}`}
                  </Text>
                </Box>
              </RawButton>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default EventTypeDropdown;
